import type { SupabaseClient } from "@supabase/supabase-js";

import { INVITE_ROLES, type InviteRole } from "./member-invites";

// Platform membership ops invite history. This is deliberately separate from
// management_member_invites so the shipped pending list and people search remain pending-only.
export const MEMBER_INVITE_HISTORY_RPC = "list_management_member_invite_history";
export const MEMBER_INVITE_HISTORY_LIMIT = 50;
export const SAFE_MEMBER_INVITE_HISTORY_FAILURE = "Could not load recent invite history.";

export const MEMBER_INVITE_HISTORY_STATUSES = ["revoked", "expired", "accepted"] as const;
export type MemberInviteHistoryStatus = (typeof MEMBER_INVITE_HISTORY_STATUSES)[number];

export const MEMBER_INVITE_AGAIN_REASONS = [
  "Active account membership already exists for this email.",
  "Pending invite already exists for this email.",
] as const;
export type MemberInviteAgainReason = (typeof MEMBER_INVITE_AGAIN_REASONS)[number];

const MEMBER_INVITE_EMAIL_PATTERN = /^[A-Z0-9._%+\-]+@[A-Z0-9.\-]+\.[A-Z]{2,}$/i;
const RFC3339_TIMESTAMP_PATTERN =
  /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.\d{1,6})?(?:Z|[+-](\d{2}):(\d{2}))$/;

export interface MemberInviteHistoryRow {
  readonly inviteId: string;
  readonly email: string;
  readonly displayName: string | null;
  readonly role: InviteRole;
  readonly status: MemberInviteHistoryStatus;
  readonly createdAt: string;
  readonly updatedAt: string | null;
  readonly canInviteAgain: boolean;
  readonly inviteAgainReason: MemberInviteAgainReason | null;
}

export interface MemberInviteHistoryPage {
  readonly rows: readonly MemberInviteHistoryRow[];
  readonly capped: boolean;
}

function nonemptyString(value: unknown): string | null {
  return typeof value === "string" && value.trim().length > 0 ? value : null;
}

function nullableString(value: unknown): string | null | undefined {
  if (value === null) return null;
  return nonemptyString(value) ?? undefined;
}

function normalizedInviteEmail(value: unknown): string | null {
  const email = nonemptyString(value);
  if (
    email === null ||
    email !== email.trim().toLowerCase() ||
    !MEMBER_INVITE_EMAIL_PATTERN.test(email)
  ) {
    return null;
  }
  return email;
}

function isTimestamp(value: string): boolean {
  const match = RFC3339_TIMESTAMP_PATTERN.exec(value);
  if (match === null) return false;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const hour = Number(match[4]);
  const minute = Number(match[5]);
  const second = Number(match[6]);
  const offsetHour = Number(match[7] ?? 0);
  const offsetMinute = Number(match[8] ?? 0);
  const leapYear = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
  const daysInMonth = [31, leapYear ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

  return (
    month >= 1 &&
    month <= 12 &&
    day >= 1 &&
    day <= daysInMonth[month - 1]! &&
    hour <= 23 &&
    minute <= 59 &&
    second <= 59 &&
    offsetHour <= 23 &&
    offsetMinute <= 59 &&
    Number.isFinite(Date.parse(value))
  );
}

function isInviteRole(value: string): value is InviteRole {
  return (INVITE_ROLES as readonly string[]).includes(value);
}

function isHistoryStatus(value: string): value is MemberInviteHistoryStatus {
  return (MEMBER_INVITE_HISTORY_STATUSES as readonly string[]).includes(value);
}

function isInviteAgainReason(value: string): value is MemberInviteAgainReason {
  return (MEMBER_INVITE_AGAIN_REASONS as readonly string[]).includes(value);
}

function mapHistoryRow(raw: unknown): {
  row: MemberInviteHistoryRow;
  capped: boolean;
} | null {
  if (raw === null || typeof raw !== "object" || Array.isArray(raw)) return null;
  const record = raw as Record<string, unknown>;
  const inviteId = nonemptyString(record.invite_id);
  const email = normalizedInviteEmail(record.email);
  const displayName = nullableString(record.display_name);
  const role = nonemptyString(record.role);
  const status = nonemptyString(record.status);
  const createdAt = nonemptyString(record.created_at);
  const updatedAt = nullableString(record.updated_at);
  const canInviteAgain = record.can_invite_again;
  const inviteAgainReason = nullableString(record.invite_again_reason);
  const historyCapped = record.history_capped;

  if (
    inviteId === null ||
    email === null ||
    displayName === undefined ||
    role === null ||
    !isInviteRole(role) ||
    status === null ||
    !isHistoryStatus(status) ||
    createdAt === null ||
    !isTimestamp(createdAt) ||
    updatedAt === undefined ||
    (updatedAt !== null && !isTimestamp(updatedAt)) ||
    typeof historyCapped !== "boolean" ||
    inviteAgainReason === undefined
  ) {
    return null;
  }

  let eligibility: {
    canInviteAgain: boolean;
    inviteAgainReason: MemberInviteAgainReason | null;
  };
  if (canInviteAgain === true && inviteAgainReason === null) {
    eligibility = { canInviteAgain: true, inviteAgainReason: null };
  } else if (
    canInviteAgain === false &&
    inviteAgainReason !== null &&
    isInviteAgainReason(inviteAgainReason)
  ) {
    eligibility = { canInviteAgain: false, inviteAgainReason };
  } else {
    return null;
  }

  return {
    row: {
      inviteId,
      email,
      displayName,
      role,
      status,
      createdAt,
      updatedAt,
      canInviteAgain: eligibility.canInviteAgain,
      inviteAgainReason: eligibility.inviteAgainReason,
    },
    capped: historyCapped,
  };
}

/** Selected-workspace terminal invite history. Throws one fixed, value-free sentence. */
export async function fetchMemberInviteHistory(
  client: SupabaseClient,
  accountId: string,
): Promise<MemberInviteHistoryPage> {
  try {
    const { data, error } = await client.rpc(MEMBER_INVITE_HISTORY_RPC, {
      p_account_id: accountId,
    });
    if (error !== null || !Array.isArray(data)) {
      throw new Error(SAFE_MEMBER_INVITE_HISTORY_FAILURE);
    }

    const mapped = data.map(mapHistoryRow);
    if (mapped.some((item) => item === null)) {
      throw new Error(SAFE_MEMBER_INVITE_HISTORY_FAILURE);
    }

    const complete = mapped as Array<{ row: MemberInviteHistoryRow; capped: boolean }>;
    if (new Set(complete.map((item) => item.row.inviteId)).size !== complete.length) {
      throw new Error(SAFE_MEMBER_INVITE_HISTORY_FAILURE);
    }

    const capFlags = new Set(complete.map((item) => item.capped));
    if (capFlags.size > 1) {
      throw new Error(SAFE_MEMBER_INVITE_HISTORY_FAILURE);
    }

    return {
      rows: complete.slice(0, MEMBER_INVITE_HISTORY_LIMIT).map((item) => item.row),
      capped:
        complete.length > MEMBER_INVITE_HISTORY_LIMIT ||
        (complete.length > 0 && complete[0]!.capped),
    };
  } catch {
    throw new Error(SAFE_MEMBER_INVITE_HISTORY_FAILURE);
  }
}
